import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import {
  type ClientsAdminV2Query,
  type ClientsV2LookupQuery,
  clientsAdminV2Service
} from '../services/clients-admin-v2.service'

type UseClientsAdminV2QueryArgs = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize: number
}

/**
 * Listado de clientes v2 con paginación por cursor y navegación prev/next por
 * PILA (ADR 0019). Espeja los hooks de la ola: la pila guarda el cursor `after`
 * de cada página; goNext apila el `nextCursor`, goPrev desapila; cambiar
 * filtros/orden/búsqueda/tamaño reinicia la pila (ajuste de estado en render).
 *
 * No hay «ir a la página 7»: el cursor es keyset, no offset. A cambio, una
 * página no se corre ni repite filas cuando alguien da de alta un cliente
 * mientras el asesor navega, que es lo que sí le pasaba al offset.
 */
export function useClientsAdminV2Query({
  filters,
  sort,
  search,
  pageSize
}: UseClientsAdminV2QueryArgs) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''

  const resetKey = JSON.stringify({ filters, sort, search, pageSize })
  const [cursorState, setCursorState] = useState<{
    key: string
    stack: (string | undefined)[]
  }>({ key: resetKey, stack: [undefined] })
  if (cursorState.key !== resetKey) {
    setCursorState({ key: resetKey, stack: [undefined] })
  }
  const after = cursorState.stack[cursorState.stack.length - 1]

  const listQuery: ClientsAdminV2Query = { filters, sort, search, pageSize, after }
  const query = useQuery({
    queryKey: queryKeys.clients.adminListV2({ filters, sort, search, pageSize, after }),
    queryFn: async () => await clientsAdminV2Service.list(accessToken, listQuery),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    // Un 403/500 es determinista: reintentarlo sólo retrasa el aviso al usuario.
    retry: false
  })

  const nextCursor = query.data?.pagination?.nextCursor
  const goNext = useCallback(() => {
    if (!nextCursor) return
    setCursorState((state) => ({ ...state, stack: [...state.stack, nextCursor] }))
  }, [nextCursor])

  const goPrev = useCallback(() => {
    setCursorState((state) =>
      state.stack.length > 1 ? { ...state, stack: state.stack.slice(0, -1) } : state
    )
  }, [])

  return {
    query,
    rows: query.data?.data ?? [],
    page: cursorState.stack.length,
    canPrev: cursorState.stack.length > 1,
    canNext: query.data?.pagination?.hasMore ?? false,
    goNext,
    goPrev
  }
}

/**
 * Totales y facetas del listado v2 (mismos filtros, sin paginar).
 *
 * Cada faceta trae su propio `total`, que NO es la suma de sus valores: cuenta
 * también las filas sin valor en esa dimensión (un cliente sin flujo abierto no
 * aparece en ningún `flowStatus` pero existe). Es lo que necesita el chip
 * «Todos» para no mentir.
 */
export function useClientsAdminV2Summary(args: {
  filters?: Record<string, unknown>
  search?: string
}) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.clients.summaryV2(args),
    queryFn: async () => await clientsAdminV2Service.summary(accessToken, args),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false
  })
}

/** Expediente completo por `cli_`, para el panel de detalle. */
export function useClientV2DetailQuery(id: string | null) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.clients.detailV2(id),
    queryFn: async () => await clientsAdminV2Service.detail(accessToken, id as string),
    enabled: Boolean(accessToken) && Boolean(id),
    refetchOnWindowFocus: false,
    retry: false
  })
}

/**
 * Borrado del expediente por v2, listo para inyectar en las acciones del panel.
 * Devuelve la función y no una mutación porque quien la recibe ya tiene la suya
 * —con su confirmación, su toast y su invalidación—: acá sólo se resuelve el
 * token de la sesión v2.
 */
export function useClientV2Remove() {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useCallback(
    async (id: string) => {
      await clientsAdminV2Service.remove(accessToken, id)
    },
    [accessToken]
  )
}

/** Selector de clientes (`cli_`, código, nombre, NIT, asesor). No paginado. */
export function useClientsV2LookupQuery(query: ClientsV2LookupQuery = {}) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.clients.lookupV2(query as Record<string, unknown>),
    queryFn: async () => await clientsAdminV2Service.lookup(accessToken, query),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false
  })
}
